/**
 * GraphQL queries and mutations for content references
 * @module content-detail/content-references/queries
 */
import { gql } from '@apollo/client'

/**
 * Search for content to use as references
 * Used by the autocomplete field to find admin categories (or other content types)
 * Uses ilike for case-insensitive search (matches YI pattern)
 */
export const SEARCH_CONTENT = gql`
  query SearchContent(
    $contentType: String!
    $bundle: String
    $title: String
    $filterType: String
    $limit: Int = 50
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(
      where: {
        contentType: { eq: $contentType }
        bundle: { eq: $bundle }
        title: { ilike: $title }
        filterType: { eq: $filterType }
      }
      language: $language
      limit: $limit
      order: [["title", "ASC"]]
    ) {
      content {
        id
        contentId
        contentType
        title
        published
        titleGroupId
      }
    }
  }
`

/**
 * Flexible content search using ContentWhereInput.
 * Used by ContentAutocomplete for all searches (single or multiple content types, filters, etc.).
 */
export const SEARCH_CONTENT_BY_TYPES = gql`
  query SearchContentByTypes(
    $where: ContentWhereInput
    $limit: Int = 50
    $language: [String] = ["en", "_primary"]
    $imageTypes: [String] = ["d6-keyart-no-text"]
  ) {
    contentList(where: $where, language: $language, limit: $limit, order: [["title", "ASC"]]) {
      content {
        id
        contentId
        contentType
        title
        published
        titleGroupId
        language
        bundle
        imageContentRelationships(language: $language, types: $imageTypes) {
          id
          type
          image {
            id
            url
          }
        }
      }
    }
  }
`

/**
 * Create a single content reference
 * Used when adding individual content references
 */
export const CREATE_CONTENT_REFERENCE = gql`
  mutation CreateContentReference($contentReference: ContentReferenceCreateInput!) {
    contentReferenceCreate(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`

/**
 * Create multiple content references in a batch
 * Used when saving new content reference selections
 *
 * IMPORTANT: The deleteOmittedReferences parameter controls deletion behavior:
 * - false (default): Only create/update the provided references, leave other references intact
 * - true: DELETE all existing references of the same type(s) that aren't in this batch
 *
 * Use deleteOmittedReferences: false when updating a subset of references to avoid
 * accidentally clobbering unrelated content references.
 */
export const CREATE_CONTENT_REFERENCES = gql`
  mutation CreateContentReferences(
    $contentReferences: [ContentReferenceCreateInput!]!
    $deleteOmittedReferences: Boolean = false
  ) {
    contentReferenceCreateMany(
      contentReferences: $contentReferences
      deleteOmittedReferences: $deleteOmittedReferences
    ) {
      contentReferences {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`

/**
 * Delete a single content reference
 * Used when removing a content reference selection
 */
export const DELETE_CONTENT_REFERENCE = gql`
  mutation DeleteContentReference($contentReference: ContentReferenceDeleteInput!) {
    contentReferenceDelete(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
      }
    }
  }
`
